import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { PromptSelect } from '../../Models/components/PromptSelect';

import { CustomerSelect } from '@/components/CustomerSelect';
import { FormGroup } from '@/components/FormGroup';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/contexts/AuthContext';
import { useCreatePrompt } from '@/hooks/useCreatePrompt';
import { Prompt } from '@/services/PromptsService';

const clonePromptSchema = z.object({
  promptId: z.string().min(1, 'Prompt is required'),
  customer: z.string().optional(),
  newPromptName: z.string().min(1, 'Prompt name is required'),
});

type ClonePromptFormData = z.infer<typeof clonePromptSchema>;

interface ClonePromptFormProps {
  open: boolean;
  onClose: () => void;
}

export function ClonePromptForm({ open, onClose }: ClonePromptFormProps) {
  const { user } = useAuth();
  const contextualCustomerId = user?.customer?.id;
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | undefined>();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<ClonePromptFormData>({
    resolver: zodResolver(clonePromptSchema),
    defaultValues: {
      promptId: '',
      customer: contextualCustomerId ? String(contextualCustomerId) : 'global',
      newPromptName: '',
    },
  });

  const { createPrompt, isLoading } = useCreatePrompt();

  async function onSubmit(data: ClonePromptFormData) {
    try {
      if (!selectedPrompt) {
        toast.error('Please select a prompt to clone');
        return;
      }

      const selectedCustomerId = data.customer
        ? Number.parseInt(data.customer, 10)
        : Number.NaN;
      const parsedCustomerId =
        contextualCustomerId ??
        (Number.isFinite(selectedCustomerId) && selectedCustomerId > 0
          ? selectedCustomerId
          : undefined);

      const promptData = {
        promptName: data.newPromptName,
        serviceName: selectedPrompt.serviceName,
        template: selectedPrompt.template,
        ...(parsedCustomerId ? { customerId: parsedCustomerId } : {}),
        responseSchema: selectedPrompt.responseSchema,
        main: false,
      };

      await createPrompt(promptData);
      toast.success('Prompt cloned successfully!');
      reset();
      setSelectedPrompt(undefined);
      onClose();
    } catch (error) {
      toast.error(
        error instanceof AxiosError
          ? error.response?.data?.message
          : 'Failed to clone prompt. Please try again.',
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose} modal={false}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Clone Prompt</DialogTitle>
          <DialogDescription>
            Select a prompt to clone and choose the customer destination.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <FormGroup error={{ message: errors.promptId?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="promptId">Prompt</RequiredLabel>
            <Controller
              name="promptId"
              control={control}
              render={({ field }) => (
                <PromptSelect
                  value={field.value}
                  customerId={contextualCustomerId}
                  onChange={(promptId, prompt) => {
                    field.onChange(promptId ? String(promptId) : '');
                    setSelectedPrompt(prompt);
                    setValue('newPromptName', prompt?.promptName ?? '', {
                      shouldValidate: true,
                    });
                  }}
                />
              )}
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="customer" className="text-sm font-medium">
              Customer Destination
            </label>
            <Controller
              name="customer"
              control={control}
              render={({ field }) => (
                <CustomerSelect
                  value={
                    contextualCustomerId
                      ? String(contextualCustomerId)
                      : (field.value ?? 'global')
                  }
                  onValueChange={field.onChange}
                  additionalOptions={[
                    { value: 'global', label: 'Global (sem customer)' },
                  ]}
                  disabled={Boolean(contextualCustomerId)}
                />
              )}
            />
          </FormGroup>

          <FormGroup
            error={{ message: errors.newPromptName?.message, font: 'sm' }}
          >
            <RequiredLabel htmlFor="newPromptName">Prompt Name</RequiredLabel>
            <Controller
              name="newPromptName"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Select a prompt to fill the name"
                />
              )}
            />
          </FormGroup>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                setSelectedPrompt(undefined);
                onClose();
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Cloning...' : 'Clone Prompt'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
